import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  Button,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import AppConfig from "appConfig";

function PromotionPage() {
  const pageTitle = "Spin & Win - Promotion";
  usePageTitle(pageTitle);

  const navigate = useNavigate();
  const { ref } = useParams();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");

  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);

  const [promotion, setPromotion] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openCreateWheelDialog, setOpenCreateWheelDialog] = useState(false);
  const [openDeleteWheelDialog, setOpenDeleteWheelDialog] = useState(false);

  const [promotionTitle, setPromotionTitle] = useState("");
  const [promotionDescription, setPromotionDescription] = useState("");
  const [promotionExpiry, setPromotionExpiry] = useState("");
  const [promotionBanner, setPromotionBanner] = useState(null);

  const [wheelTitle, setWheelTitle] = useState("");
  const [wheelDescription, setWheelDescription] = useState("");
  const [wheelWinProbability, setWheelWinProbability] = useState("0");
  const [wheelSpinAttempts, setWheelSpinAttempts] = useState("1");
  const [wheelSpinInterval, setWheelSpinInterval] = useState("");

  const [selectedWheel, setSelectedWheel] = useState(null);

  const [participants, setParticipants] = useState([]);
  const [participantsLoaded, setParticipantsLoaded] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrMsg(message || "An unexpected error occurred.");
    setOpen(true);
  };

  const handleRequestError = () => {
    setShowLoading(false);

    showMessage(
      `Unable to complete request. ${
        navigator.onLine ? "" : "Make sure you have an active internet connection."
      }`
    );
  };

  const getExpiryInputValue = (value) => {
    if (!value || value === "---") {
      return "";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, "0"),
      String(date.getDate()).padStart(2, "0"),
    ].join("-");
  };

  const fetchPromotion = async () => {
    setShowLoading(true);

    try {
      const result = await axios.get(`/portal/tm/spin/promotions/${ref}/fetch.json`);

      if (result.data.status === "success") {
        const data = result.data.data.promotion;

        setPromotion(data);
        setPromotionTitle(data.title || "");
        setPromotionDescription(data.description || "");
        setPromotionExpiry(getExpiryInputValue(data.expires_on));
        setPageLoaded(true);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  async function updatePromotionStatus() {
    setShowLoading(true);

    await axios
      .get(`portal/tm/spin/promotions/${ref}/status.json`)
      .then((result) => {
        setShowLoading(false);

        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const fetchParticipants = async () => {
    setShowLoading(true);

    try {
      const result = await axios.get(`/portal/tm/spin/promotions/${ref}/participants.json`);

      if (result.data.status === "success") {
        const data = result.data.data.participants || [];

        const rows = data.map((row) => ({
          participant: row.name || row.title || row.reference || "---",
          phone_number: row.phone_number || "---",
          created: row.created || "---",
        }));

        setParticipants(rows);
        setParticipantsLoaded(true);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  useEffect(() => {
    if (ref) {
      fetchPromotion();
    }
  }, [ref, refreshPage]);

  useEffect(() => {
    if (activeTab === 1 && ref) {
      fetchParticipants();
    }
  }, [activeTab, ref, refreshPage]);

  const resetWheelForm = () => {
    setWheelTitle("");
    setWheelDescription("");
    setWheelWinProbability("0");
    setWheelSpinAttempts("1");
    setWheelSpinInterval("");
  };

  const resetPromotionForm = () => {
    if (!promotion) {
      return;
    }

    setPromotionTitle(promotion.title || "");
    setPromotionDescription(promotion.description || "");
    setPromotionExpiry(getExpiryInputValue(promotion.expires_on));
    setPromotionBanner(null);
  };

  const editPromotion = async () => {
    if (!promotionTitle.trim()) {
      showMessage("Enter promotion title.");
      return;
    }

    const formData = new FormData();

    formData.append("reference", ref);
    formData.append("title", promotionTitle.trim());
    formData.append("description", promotionDescription);

    if (promotionExpiry) {
      const expiryDate = new Date(`${promotionExpiry}T23:59:59`);

      if (Number.isNaN(expiryDate.getTime())) {
        showMessage("Enter a valid expiry date.");
        return;
      }

      const expiryTimestamp = Math.floor(expiryDate.getTime() / 1000);

      formData.append("expires_on", String(expiryTimestamp));
    }

    if (promotionBanner) {
      formData.append("banner", promotionBanner);
    }

    setOpenEditDialog(false);
    setShowLoading(true);

    try {
      const result = await axios.post("/portal/tm/spin/promotions/edit.json", formData);

      if (result.data.status === "success") {
        showMessage(result.data.data.message, "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const createWheel = async () => {
    const title = wheelTitle.trim();
    const winProbability = Number(wheelWinProbability);
    const spinAttempts = Number(wheelSpinAttempts);

    // An interval only applies when more than one spin attempt is configured.
    const spinInterval =
      spinAttempts > 1 && wheelSpinInterval.trim() !== "" ? Number(wheelSpinInterval) : null;

    if (!title) {
      showMessage("Enter wheel title.");
      return;
    }

    if (
      wheelWinProbability.trim() === "" ||
      !Number.isFinite(winProbability) ||
      winProbability < 0 ||
      winProbability > 100
    ) {
      showMessage("Win probability must be between 0 and 100.");
      return;
    }

    if (wheelSpinAttempts.trim() === "" || !Number.isInteger(spinAttempts) || spinAttempts < 0) {
      showMessage("Spin attempts must be a non-negative whole number.");
      return;
    }

    if (spinInterval !== null && (!Number.isInteger(spinInterval) || spinInterval < 0)) {
      showMessage("Spin interval must be a non-negative whole number or left empty.");
      return;
    }

    setOpenCreateWheelDialog(false);
    setShowLoading(true);

    const payload = {
      promotion: ref,
      title,
      description: wheelDescription,
      win_probability: winProbability,
      spin_attempts: spinAttempts,
      spin_interval: spinInterval,
    };

    try {
      const result = await axios.post("/portal/tm/spin/promotions/wheels/create.json", payload);
      if (result.data.status === "success") {
        resetWheelForm();
        showMessage(result.data.data.message, "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const updateWheelStatus = async (wheel) => {
    setShowLoading(true);

    try {
      const result = await axios.get(
        `/portal/tm/spin/promotions/wheels/${wheel.reference}/status.json`
      );
      if (result.data.status === "success") {
        showMessage(result.data.data.message, "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const deleteWheel = async () => {
    if (!selectedWheel) {
      return;
    }

    setOpenDeleteWheelDialog(false);
    setShowLoading(true);

    try {
      const result = await axios.get(
        `/portal/tm/spin/promotions/wheels/${selectedWheel.reference}/delete.json`
      );
      if (result.data.status === "success") {
        setSelectedWheel(null);
        showMessage(result.data.data.message, "success");
        setRefreshPage((previous) => !previous);
      } else {
        showMessage(result.data.data.message);
      }
    } catch (error) {
      handleRequestError();
      return;
    }

    setShowLoading(false);
  };

  const wheelColumns = [
    {
      Header: "Wheel",
      accessor: "title",
      align: "left",
    },
    {
      Header: "Segments",
      accessor: "segments",
      align: "center",
    },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
    },
    {
      Header: "Actions",
      accessor: "actions",
      align: "right",
    },
  ];

  const participantColumns = [
    {
      Header: "Participant",
      accessor: "participant",
      align: "left",
    },
    {
      Header: "Phone Number",
      accessor: "phone_number",
      align: "left",
    },
    {
      Header: "Created",
      accessor: "created",
      align: "right",
    },
  ];

  const buildWheelRows = (data) => {
    return data.map((row) => {
      const segments = row.segments || [];

      return {
        title: (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="button" fontWeight="medium">
              {row.title}
            </MDTypography>

            <MDTypography variant="caption" color="text">
              {row.description || "---"}
            </MDTypography>
          </MDBox>
        ),

        segments: Array.isArray(segments) ? segments.length : segments,

        status: (
          <MDTypography
            variant="button"
            fontWeight="medium"
            color={row.status ? "success" : "text"}
          >
            {row.status ? "Active" : "Inactive"}
          </MDTypography>
        ),

        actions: (
          <MDBox mt={1} variant="button" display="flex" gap={1} justifyContent="flex-end">
            <Tooltip title="Manage Wheel">
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                onClick={() => navigate(`/tm/spin/promotions/wheels/${row.reference}`)}
                iconOnly
              >
                <Icon>settings</Icon>
              </MDButton>
            </Tooltip>

            <Tooltip title={row.status ? "Deactivate Wheel" : "Activate Wheel"}>
              <MDButton
                size="small"
                variant="outlined"
                color={row.status ? "warning" : "success"}
                iconOnly
                onClick={() => updateWheelStatus(row)}
              >
                <Icon fontSize="small">{row.status ? "pause" : "play_arrow"}</Icon>
              </MDButton>
            </Tooltip>

            <Tooltip title="Delete Wheel">
              <MDButton
                size="small"
                variant="outlined"
                color="error"
                iconOnly
                onClick={() => {
                  setSelectedWheel(row);
                  setOpenDeleteWheelDialog(true);
                }}
              >
                <Icon fontSize="small">delete</Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
      };
    });
  };

  const wheels = Array.isArray(promotion?.wheels) ? promotion.wheels : [];
  const wheelRows = buildWheelRows(wheels);

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>

      <Dialog
        open={openEditDialog}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          setOpenEditDialog(false);
          resetPromotionForm();
        }}
      >
        <DialogTitle>Edit Promotion</DialogTitle>

        <DialogContent>
          <MDBox>
            <MDBox mt={1}>
              <MDInput
                name="title"
                margin="dense"
                label="Promotion Title"
                type="text"
                fullWidth
                variant="outlined"
                value={promotionTitle}
                onChange={(event) => setPromotionTitle(event.target.value)}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="description"
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={promotionDescription}
                onChange={(event) => setPromotionDescription(event.target.value)}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="expires_on"
                margin="dense"
                label="Expiry Date"
                type="date"
                fullWidth
                variant="outlined"
                value={promotionExpiry}
                onChange={(event) => setPromotionExpiry(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </MDBox>

            <MDBox mt={3}>
              <MDTypography variant="button" fontWeight="medium">
                Replace Banner
              </MDTypography>

              <MDBox mt={1}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(event) => {
                    const files = event.target.files;
                    setPromotionBanner(files && files.length > 0 ? files[0] : null);
                  }}
                />
              </MDBox>
            </MDBox>
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenEditDialog(false);
              resetPromotionForm();
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={editPromotion}>
            Save Changes
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openCreateWheelDialog}
        fullWidth
        maxWidth="sm"
        onClose={() => {
          setOpenCreateWheelDialog(false);
          resetWheelForm();
        }}
      >
        <DialogTitle>Create Wheel</DialogTitle>

        <DialogContent>
          <MDBox>
            <MDBox mt={1}>
              <MDInput
                name="title"
                margin="dense"
                label="Wheel Title"
                type="text"
                fullWidth
                variant="outlined"
                value={wheelTitle}
                onChange={(event) => setWheelTitle(event.target.value)}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="description"
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={wheelDescription}
                onChange={(event) => setWheelDescription(event.target.value)}
              />
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="win_probability"
                margin="dense"
                label="Win Probability (%)"
                type="number"
                fullWidth
                variant="outlined"
                value={wheelWinProbability}
                onChange={(event) => setWheelWinProbability(event.target.value)}
                inputProps={{
                  min: 0,
                  max: 100,
                  step: "any",
                }}
              />

              <MDTypography variant="caption" color="text">
                Enter a value between 0 and 100.
              </MDTypography>
            </MDBox>

            <MDBox mt={2}>
              <MDInput
                name="spin_attempts"
                margin="dense"
                label="Spin Attempts"
                type="number"
                fullWidth
                variant="outlined"
                value={wheelSpinAttempts}
                onChange={(event) => {
                  const value = event.target.value;
                  setWheelSpinAttempts(value);

                  if (value === "" || Number(value) <= 1) {
                    setWheelSpinInterval("");
                  }
                }}
                inputProps={{
                  min: 0,
                  step: 1,
                }}
              />
            </MDBox>

            {Number(wheelSpinAttempts) > 1 && (
              <MDBox mt={2}>
                <MDInput
                  name="spin_interval"
                  margin="dense"
                  label="Spin Interval (Minutes)"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={wheelSpinInterval}
                  onChange={(event) => setWheelSpinInterval(event.target.value)}
                  inputProps={{
                    min: 0,
                    step: 1,
                  }}
                />

                <MDTypography variant="caption" color="text">
                  Wait time between spins. Applies only when attempts is greater than 1.
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>

        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenCreateWheelDialog(false);
              resetWheelForm();
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>

          <MDButton color="dark" variant="contained" onClick={createWheel}>
            Create Wheel
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteWheelDialog}
        fullWidth
        maxWidth="xs"
        onClose={() => setOpenDeleteWheelDialog(false)}
      >
        <DialogTitle>Delete Wheel</DialogTitle>

        <DialogContent>
          <MDTypography variant="button" color="text">
            Are you sure you want to delete <strong>{selectedWheel?.title}</strong>?
          </MDTypography>
        </DialogContent>

        <DialogActions>
          <MDButton onClick={() => setOpenDeleteWheelDialog(false)} color="dark" variant="outlined">
            Cancel
          </MDButton>

          <MDButton color="error" variant="contained" onClick={deleteWheel}>
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>

      <MDBox mt={1} mb={5}>
        {pageLoaded && promotion && (
          <MDBox>
            <Card sx={{ height: "100%", marginTop: "0px" }}>
              <MDBox p={3}>
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                  flexWrap="wrap"
                  gap={2}
                >
                  <MDBox>
                    <MDTypography variant="h5" fontWeight="bold">
                      {promotion.title}
                    </MDTypography>

                    <MDTypography mt={1} variant="button" fontWeight="light" color="text">
                      {promotion.description || "No description provided."}
                    </MDTypography>

                    <MDBox mt={2} display="flex" gap={1} alignItems="center">
                      <MDTypography
                        variant="button"
                        fontWeight="medium"
                        color={promotion.status ? "success" : "text"}
                      >
                        {promotion.status ? "Active" : "Inactive"}
                      </MDTypography>

                      <MDTypography variant="caption" color="text">
                        | Expires: {promotion.expires_on || "---"}
                      </MDTypography>
                    </MDBox>
                  </MDBox>

                  <MDBox display="flex" gap={1} flexWrap="wrap">
                    <Tooltip title="Back to Promotions">
                      <MDButton
                        size="small"
                        variant="outlined"
                        color="dark"
                        iconOnly
                        onClick={() => navigate("/tm/spin/promotions")}
                      >
                        <Icon>west</Icon>
                      </MDButton>
                    </Tooltip>

                    <Tooltip title={promotion.status ? "Deactivate" : "Activate"}>
                      <MDButton
                        size="small"
                        variant="outlined"
                        color={promotion.status ? "warning" : "success"}
                        iconOnly
                        onClick={() => {
                          updatePromotionStatus();
                        }}
                      >
                        <Icon sx={{ cursor: "pointer" }} fontSize="small">
                          {promotion.status ? "pause" : "play_arrow"}
                        </Icon>
                      </MDButton>
                    </Tooltip>

                    <Tooltip title="Edit Promotion">
                      <MDButton
                        variant="contained"
                        color="dark"
                        iconOnly
                        onClick={() => {
                          resetPromotionForm();
                          setOpenEditDialog(true);
                        }}
                        size="small"
                      >
                        <Icon>edit</Icon>
                      </MDButton>
                    </Tooltip>
                  </MDBox>
                </MDBox>

                {promotion.banner && (
                  <MDBox mt={3}>
                    <MDTypography variant="button" fontWeight="medium">
                      Promotion Banner
                    </MDTypography>

                    <MDBox mt={1}>
                      <img
                        src={`${AppConfig.api_media_url}${promotion.banner}`}
                        alt={promotion.title}
                        style={{
                          maxWidth: 120,
                          maxHeight: 260,
                          objectFit: "contain",
                          borderRadius: 4,
                        }}
                      />
                    </MDBox>
                  </MDBox>
                )}
              </MDBox>
            </Card>

            <MDBox mt={3}>
              <Card>
                <MDBox px={2} pt={2}>
                  <Tabs
                    value={activeTab}
                    onChange={(event, value) => setActiveTab(value)}
                    textColor="primary"
                    indicatorColor="primary"
                  >
                    <Tab label="Spin Wheels" />
                    <Tab label="Participants" />
                  </Tabs>
                </MDBox>

                <Divider />

                {activeTab === 0 && (
                  <MDBox>
                    <MDBox p={2} display="flex" justifyContent="space-between" alignItems="center">
                      <MDTypography variant="h6" fontWeight="medium">
                        Spin Wheels
                      </MDTypography>

                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          resetWheelForm();
                          setOpenCreateWheelDialog(true);
                        }}
                        startIcon={<Icon>add</Icon>}
                        size="small"
                      >
                        Create Wheel
                      </MDButton>
                    </MDBox>

                    {wheels.length > 0 ? (
                      <MDBox p={0}>
                        <DataTable
                          table={{
                            columns: wheelColumns,
                            rows: wheelRows,
                          }}
                          canSearch={false}
                          isSorted
                          entriesPerPage={false}
                          showTotalEntries
                          buttons
                          noEndBorder
                        />
                      </MDBox>
                    ) : (
                      <MDBox p={3}>
                        <MDTypography variant="button" color="text">
                          No wheels have been created for this promotion.
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                )}

                {activeTab === 1 && (
                  <MDBox>
                    <MDBox p={2}>
                      <MDTypography variant="h6" fontWeight="medium">
                        Participants
                      </MDTypography>
                    </MDBox>

                    {participantsLoaded && participants.length > 0 && (
                      <MDBox p={0}>
                        <DataTable
                          table={{
                            columns: participantColumns,
                            rows: participants,
                          }}
                          canSearch
                          isSorted={false}
                          entriesPerPage={false}
                          showTotalEntries
                          buttons
                          noEndBorder
                        />
                      </MDBox>
                    )}

                    {participantsLoaded && participants.length === 0 && (
                      <MDBox p={3}>
                        <MDTypography variant="button" color="text">
                          No participants have registered for this promotion.
                        </MDTypography>
                      </MDBox>
                    )}
                  </MDBox>
                )}
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

export default PromotionPage;
